/**
 * Hook for `linkedin-article-target.ts`: a `linkedin_article` row's label is
 * its publication's LinkedIn newsletter publish target, never an account.
 */
import { useApiQuery } from "@/lib/api";
import { usePieceLookup } from "@/lib/piece-lookup";
import {
  linkedinArticleLabel,
  linkedinArticleTarget,
  type PublishTargetLookupRow,
} from "@/lib/linkedin-article-target";

/**
 * The label for a `linkedin_article` row, or undefined for any other
 * platform. Hooks cannot be conditional, so this is called for every row and
 * fetches only when `platform` is `linkedin_article`.
 */
export function useLinkedinArticleLabel(
  platform: string,
  pieceId: number | null | undefined
): string | undefined {
  const isArticle = platform === "linkedin_article";
  const piece = usePieceLookup(isArticle ? (pieceId ?? null) : null);
  const publicationId = piece?.publication_id ?? piece?.publicationId ?? null;
  const { data } = useApiQuery<{ data: PublishTargetLookupRow[] }>(
    ["publish-targets-for-publication", publicationId ?? 0],
    `/publish-targets?publication_id=${publicationId ?? 0}`,
    {
      enabled: isArticle && publicationId != null,
      staleTime: 5 * 60_000,
      retry: false,
    }
  );
  if (!isArticle) return undefined;
  return linkedinArticleLabel(linkedinArticleTarget(data?.data ?? [], publicationId));
}
